import { Flex, Box, Text } from '@chakra-ui/react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $restoreEditorState } from '@lexical/utils';
import { trimTextContentFromAnchor } from '@lexical/selection';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  INSERT_LINE_BREAK_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  RootNode,
  type EditorState,
  type LexicalEditor,
  type LexicalNode,
} from 'lexical';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  $createVariableNode,
  VariableNode,
  VariableTypeaheadPlugin,
  type AvailableVariable,
} from '@fluenceur/ui';

interface InlineVariableEditorProps {
  readOnly?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  editorKey?: number;
  /**
   * Optional plain-text content used to hydrate the editor on mount. Tokens
   * `{{variable.key}}` matching an entry in `availableVariables` render as
   * VariableNode chips; anything else is preserved as literal text. To
   * re-hydrate, bump `editorKey` so the composer remounts.
   */
  initialPlainText?: string;
  availableVariables: AvailableVariable[];
  translateFn?: (key: string) => string;
  onContentChange: (plainText: string) => void;
  children?: ReactNode;
  fontSize?: string;
  minH?: string;
  /**
   * Hard cap on the serialized subject length (chips count as their `{{key}}`
   * form). When set, keystrokes and pastes past the cap are trimmed and a
   * discreet `N / max` counter appears once the content nears the limit. Omit on
   * body/DM surfaces that are not length-bound at this layer.
   */
  maxLength?: number;
}

// The counter stays hidden until the subject is within reach of the cap, so the
// row is quiet during normal use and only surfaces the limit when it matters.
const COUNTER_VISIBLE_FROM = 200;

/**
 * Registers a RootNode transform that keeps the editor's serialized text within
 * `maxLength`. Variable chips count as their `{{key}}` form — what the send rail
 * serializes — because {@link VariableNode.getTextContent} returns that string.
 *
 * Ported from Lexical's MaxLengthPlugin: it trims the overflow from the caret on
 * a keystroke and restores the prior state once the cap is already reached, so a
 * paste cannot overshoot. It acts only on a collapsed range selection, so an
 * out-of-band seed (hydration sets no selection) is preserved verbatim rather
 * than truncated on mount.
 */
export function registerMaxLength(editor: LexicalEditor, maxLength: number): () => void {
  let lastRestoredEditorState: EditorState | null = null;

  return editor.registerNodeTransform(RootNode, (rootNode) => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) return;

    const prevEditorState = editor.getEditorState();
    const prevTextContent = prevEditorState.read(() => rootNode.getTextContent());
    const textContent = rootNode.getTextContent();
    if (prevTextContent === textContent) return;

    const delCount = textContent.length - maxLength;
    if (delCount <= 0) return;

    if (prevTextContent.length === maxLength && lastRestoredEditorState !== prevEditorState) {
      // Already at the cap before this change: reject the whole insertion rather
      // than nibble it, so an IME burst or paste can't creep past the limit.
      lastRestoredEditorState = prevEditorState;
      $restoreEditorState(editor, prevEditorState);
    } else {
      trimTextContentFromAnchor(editor, selection.anchor, delCount);
    }
  });
}

/** Wires {@link registerMaxLength} into the composer lifecycle. */
function MaxLengthPlugin({ maxLength }: { maxLength: number }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => registerMaxLength(editor, maxLength), [editor, maxLength]);
  return null;
}

function SingleLinePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Use EDITOR priority (lowest) so the typeahead plugin at LOW priority
    // can handle Enter first to confirm a variable selection.
    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      () => true,
      COMMAND_PRIORITY_EDITOR
    );
    const unregisterLineBreak = editor.registerCommand(
      INSERT_LINE_BREAK_COMMAND,
      () => true,
      COMMAND_PRIORITY_HIGH
    );
    const unregisterParagraph = editor.registerCommand(
      INSERT_PARAGRAPH_COMMAND,
      () => true,
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      unregisterEnter();
      unregisterLineBreak();
      unregisterParagraph();
    };
  }, [editor]);

  return null;
}

/**
 * Identifies a variable chip by its registered node type rather than
 * `instanceof VariableNode`. The chip instances are created inside the
 * `@fluenceur/ui` bundle and may not share the exact class reference imported
 * here (duplicate-module boundary), which makes `instanceof` silently fail — the
 * root cause of the delete regression. The type string is stable across copies.
 */
function $isVariableChip(node: LexicalNode | null | undefined): node is LexicalNode {
  return node != null && node.getType() === 'variable';
}

/**
 * Removes an adjacent variable chip for a delete keystroke. Lexical's default
 * Backspace/Delete leaves the DecoratorNode in place, which made inserted
 * subject/body variables impossible to remove (a regression). Returns true when
 * it consumed the keystroke. Must run inside an editor update (command scope).
 *
 * @param backwards true for Backspace (look at the previous sibling), false for
 * Delete (look at the next sibling).
 */
export function $removeAdjacentVariable(backwards: boolean): boolean {
  const selection = $getSelection();

  // The chip itself is selected (keyboard navigation) — remove it.
  if ($isNodeSelection(selection)) {
    const variable = selection.getNodes().find($isVariableChip);
    if (variable) {
      variable.remove();
      return true;
    }
    return false;
  }

  // Only act on a collapsed caret sitting flush against a chip; otherwise let
  // Lexical's default handler delete the selection/character.
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;

  const anchor = selection.anchor;
  const node = anchor.getNode();

  // The caret can resolve either to a text node (offset = character index) or to
  // the paragraph itself (offset = child index) when it sits between a chip and
  // a sibling. Handle both so the chip is always reachable.
  let target: LexicalNode | null = null;
  if ($isElementNode(node)) {
    target = backwards
      ? node.getChildAtIndex(anchor.offset - 1)
      : node.getChildAtIndex(anchor.offset);
  } else {
    target = backwards
      ? anchor.offset === 0
        ? node.getPreviousSibling()
        : null
      : anchor.offset === node.getTextContentSize()
        ? node.getNextSibling()
        : null;
  }

  if ($isVariableChip(target)) {
    target.remove();
    return true;
  }
  return false;
}

/**
 * Wires Backspace/Delete to {@link $removeAdjacentVariable} so variable chips can
 * be deleted from the editor.
 */
function VariableDeletePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Preempt Lexical's default delete handler; because we take over, we must
    // also preventDefault so the browser doesn't run its own (conflicting)
    // Backspace on the contenteditable.
    const handleKey = (backwards: boolean) => (event: KeyboardEvent | null) => {
      const handled = $removeAdjacentVariable(backwards);
      if (handled) event?.preventDefault();
      return handled;
    };

    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      handleKey(true),
      COMMAND_PRIORITY_HIGH
    );
    const unregisterDelete = editor.registerCommand(
      KEY_DELETE_COMMAND,
      handleKey(false),
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      unregisterBackspace();
      unregisterDelete();
    };
  }, [editor]);

  return null;
}

/**
 * A long single-line value stays scrolled to wherever the caret last was; on
 * blur the reader expects to see the BEGINNING of the value, so snap back.
 */
function ScrollToStartOnBlurPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const scrollToStart = (event: FocusEvent) => {
      (event.currentTarget as HTMLElement).scrollLeft = 0;
    };

    return editor.registerRootListener((rootElement, previousRootElement) => {
      previousRootElement?.removeEventListener('blur', scrollToStart);
      rootElement?.addEventListener('blur', scrollToStart);
    });
  }, [editor]);

  return null;
}

function FocusOnPointerDownPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const focusEditor = () => {
      editor.focus();
    };

    return editor.registerRootListener((rootElement, previousRootElement) => {
      previousRootElement?.removeEventListener('pointerdown', focusEditor);
      rootElement?.addEventListener('pointerdown', focusEditor);
    });
  }, [editor]);

  return null;
}

const VARIABLE_TOKEN_REGEX = /\{\{([a-zA-Z0-9_.-]+)\}\}/g;

/**
 * Builds a Lexical init function that parses `{{key}}` tokens in
 * `initialPlainText` into VariableNode chips. Tokens whose key isn't in
 * `availableVariables` are preserved as literal text so content round-trips.
 */
function buildInitialState(
  initialPlainText: string | undefined,
  availableVariables: AvailableVariable[]
): ((editor: LexicalEditor) => void) | undefined {
  if (!initialPlainText || initialPlainText.length === 0) return undefined;

  const knownKeys = new Set<string>();
  for (const v of availableVariables) {
    knownKeys.add(v.key);
    if (v.value) knownKeys.add(v.value);
  }

  return (editor) => {
    editor.update(() => {
      const root = $getRoot();
      if (root.getChildrenSize() > 0) root.clear();

      const paragraph = $createParagraphNode();
      let lastIndex = 0;
      VARIABLE_TOKEN_REGEX.lastIndex = 0;

      let match: RegExpExecArray | null;
      while ((match = VARIABLE_TOKEN_REGEX.exec(initialPlainText)) !== null) {
        const [fullToken, key] = match;
        const start = match.index;

        if (start > lastIndex) {
          paragraph.append($createTextNode(initialPlainText.slice(lastIndex, start)));
        }

        if (knownKeys.has(key)) {
          paragraph.append($createVariableNode(key));
        } else {
          paragraph.append($createTextNode(fullToken));
        }

        lastIndex = start + fullToken.length;
      }

      if (lastIndex < initialPlainText.length) {
        paragraph.append($createTextNode(initialPlainText.slice(lastIndex)));
      }

      root.append(paragraph);
    });
  };
}

const INLINE_EDITOR_THEME = {
  paragraph: 'editor-paragraph',
};

// Same fade the app already uses on overflowing text (see CreatorAvatar's
// FADED_TEXT_MASK): the right edge of a too-long value melts to transparent.
// Only at rest — while focused the caret area must stay fully visible.
const OVERFLOW_FADE_MASK = 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)';

function InlineVariableEditor({
  placeholder,
  ariaLabel,
  editorKey,
  initialPlainText,
  availableVariables,
  translateFn,
  onContentChange,
  children,
  fontSize = '14px',
  minH = '32px',
  maxLength,
  readOnly = false,
}: InlineVariableEditorProps) {
  const [length, setLength] = useState(initialPlainText?.length ?? 0);

  const initialConfig = useMemo(
    () => ({
      namespace: `InlineVariableEditor-${editorKey ?? 0}`,
      editable: !readOnly,
      nodes: [VariableNode],
      theme: INLINE_EDITOR_THEME,
      editorState: buildInitialState(initialPlainText, availableVariables),
      onError: (error: Error) => {
        console.error('InlineVariableEditor error:', error);
      },
    }),
    // initialPlainText/availableVariables are captured at composer mount only;
    // to re-hydrate, bump editorKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorKey, readOnly]
  );

  const handleChange = useCallback(
    (editorState: EditorState) => {
      if (readOnly) return;
      editorState.read(() => {
        const root = $getRoot();
        const parts: string[] = [];
        for (const child of root.getChildren()) {
          const text = child.getTextContent();
          if (text) {
            parts.push(text);
          }
        }
        const plainText = parts.join('');
        onContentChange(plainText);
        // Only bounded surfaces need the live count; leave others re-render-free.
        if (maxLength != null) setLength(plainText.length);
      });
    },
    [onContentChange, maxLength, readOnly]
  );

  const showCounter = maxLength != null && length >= COUNTER_VISIBLE_FROM;

  return (
    <LexicalComposer key={editorKey} initialConfig={initialConfig}>
      <Flex align="center" flex={1} minW={0} minH={minH} gap={2}>
        <Box
          flex={1}
          minW={0}
          h={minH}
          position="relative"
          display="flex"
          alignItems="center"
          cursor="text"
          rounded="sm"
          css={{
            '& .editor-input .editor-paragraph': {
              margin: 0,
              minHeight: minH,
              lineHeight: minH,
              // Lexical wraps the content in a <p>; force single-line here too so
              // a long subject cannot wrap and paint over the modal even if a
              // theme reset were to override the inherited white-space. `pre`,
              // not `nowrap`: nowrap collapses spaces, so the space typed after a
              // variable chip had no width and the caret parked in it vanished.
              whiteSpace: 'pre',
            },
            '& .editor-input:not(:focus)': {
              maskImage: OVERFLOW_FADE_MASK,
              WebkitMaskImage: OVERFLOW_FADE_MASK,
            },
          }}
        >
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className="editor-input"
                style={{
                  width: '100%',
                  minHeight: minH,
                  display: 'block',
                  position: 'relative',
                  zIndex: 1,
                  outline: 'none',
                  fontSize,
                  // Single-line by construction: never wrap, keep the caret and
                  // any long unbroken string scrolling INSIDE the box (minW:0 on
                  // the flex parents bounds the width) so nothing paints over the
                  // rest of the modal. `pre` keeps every typed space visible
                  // (Lexical needs preserved whitespace; see the paragraph rule).
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  lineHeight: minH,
                  caretColor: 'currentColor',
                  cursor: 'text',
                }}
                ariaLabel={ariaLabel ?? placeholder}
              />
            }
            placeholder={
              placeholder ? (
                <Box
                  position="absolute"
                  top={0}
                  bottom={0}
                  left={0}
                  right={0}
                  display="flex"
                  alignItems="center"
                  // Reserve room for the optional trailing affordance (e.g.
                  // VariableDropdownPlugin) so placeholder doesn't slide under it.
                  pr={children ? 8 : 0}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  color="fg.subtle"
                  fontSize={fontSize}
                  lineHeight={minH}
                  zIndex={0}
                  pointerEvents="none"
                  userSelect="none"
                >
                  {placeholder}
                </Box>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </Box>
        {showCounter ? (
          <Text flexShrink={0} fontSize="xs" color="fg.muted" whiteSpace="nowrap">
            {length} / {maxLength}
          </Text>
        ) : null}
        {!readOnly && children}
      </Flex>

      <OnChangePlugin onChange={handleChange} />
      {!readOnly && <HistoryPlugin />}
      <SingleLinePlugin />
      <VariableDeletePlugin />
      <ScrollToStartOnBlurPlugin />
      {!readOnly && <FocusOnPointerDownPlugin />}
      {!readOnly && (
        <VariableTypeaheadPlugin
          availableVariables={availableVariables}
          translateFn={translateFn}
        />
      )}
      {maxLength != null ? <MaxLengthPlugin maxLength={maxLength} /> : null}
    </LexicalComposer>
  );
}

export default InlineVariableEditor;
export type { InlineVariableEditorProps };
